import React from 'react';
import ScrollReveal from '../../../components/ScrollReveal';

const Features = () => {
  const services = [
    {
      title: 'Web Applications',
      description: 'Custom web applications built for performance, usability, and growth. Clean architecture that scales with your business.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      gradient: 'from-primary-500 to-primary-600',
      features: ['React & Next.js', 'TypeScript', 'REST & GraphQL APIs'],
    },
    {
      title: 'Mobile Apps',
      description: 'Native and cross-platform apps for iOS and Android. Fast, secure, and designed to keep your users engaged.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      gradient: 'from-accent-orange to-accent-pink',
      features: ['React Native', 'iOS & Android', 'Offline-First'],
    },
    {
      title: 'Quality Assurance',
      description: 'Comprehensive manual and automated testing to ship with confidence. Fewer bugs, faster releases.',
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      gradient: 'from-accent-cyan to-primary-500',
      features: ['Automated Testing', 'CI/CD Integration', 'Performance Testing'],
    },
  ];

  return (
    <section id="services" className="section-py bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-primary-50 to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary-50 to-transparent opacity-50" />

      <div className="container-lg relative">
        {/* Section Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <span className="label mb-4 block">Our Services</span>
            <h2 className="heading-lg mb-6">
              Everything you need to
              <br />
              <span className="text-gradient">ship great software</span>
            </h2>
            <p className="subheading">
              From concept to deployment, we provide end-to-end development services tailored to your business needs.
            </p>
          </div>
        </ScrollReveal>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ScrollReveal key={service.title} delay={index * 100}>
              <div className="group card card-hover card-gradient p-8 h-full">
              {/* Icon */}
              <div className={`icon-box-lg bg-gradient-to-br ${service.gradient} text-white mb-6 shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-500`}>
                {service.icon}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-dark-900 mb-3 group-hover:text-primary-600 transition-colors">
                {service.title}
              </h3>
              <p className="text-dark-500 leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Tech tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {service.features.map((feature) => (
                  <span
                    key={feature}
                    className="px-3 py-1 text-xs font-medium bg-dark-100 text-dark-600 rounded-lg"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Link */}
              <a href="#contact" className="link-hover text-sm">
                Learn more
                <svg className="w-4 h-4 inline-block ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Bottom CTA */}
        <ScrollReveal>
          <div className="text-center mt-10 pt-10 border-t border-dark-100">
            <p className="text-dark-500 mb-6">
              Need a custom solution? We'd love to hear about your project.
            </p>
            <a href="#contact" className="btn btn-secondary btn-lg">
              Schedule a Free Consultation
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Features;
